import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, Copy, FilePlus2, Pencil } from 'lucide-react';
import type { FormSummary } from '@kaba/shared';
import { api } from '../../lib/api';
import { navigate } from '../../lib/router';
import { toast } from '../../lib/toast';
import { Button, cx, Sheet } from '../ui/ui';
import { STATUS_TEXT } from './FormEditorPage';

interface Tpl {
  key: string;
  name: string;
  summary: string;
  needs: { labels: { name: string }[]; fields: { name: string }[] };
  questions: number;
}

/** Boardmenü → Formulare: Liste, Status und „Neues Formular“ (U01) */
export function FormsSheet({ boardId, columns, open, onClose }: { boardId: string; columns: { id: string; title: string }[]; open: boolean; onClose: () => void }) {
  const { data, refetch } = useQuery({ queryKey: ['forms', boardId], queryFn: () => api.get<{ canManage: boolean; forms: FormSummary[] }>(`/api/boards/${boardId}/forms`), enabled: open });
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    if (open) void refetch();
  }, [open, refetch]);
  const visible = (data?.forms ?? []).filter((f) => f.status !== 'archived');
  const archived = (data?.forms ?? []).filter((f) => f.status === 'archived');
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Formulare">
      <div className="flex flex-col gap-4 p-4">
        <p className="text-sm text-[var(--ink-soft)]">Formulare sind ein Eingang ins Board: Jede angenommene Einsendung wird eine normale Karte in der gewählten Spalte.</p>
        {data?.canManage && (
          <Button variant="primary" className="self-start" onClick={() => setCreating(true)}>
            <FilePlus2 size={16} /> Neues Formular
          </Button>
        )}
        {data && !visible.length && <p className="rounded-xl border border-dashed border-[var(--line)] p-4 text-center text-sm text-[var(--ink-soft)]">Noch keine Formulare.</p>}
        <ul className="flex flex-col gap-2">
          {[...visible, ...archived].map((f) => (
            <li key={f.id} className={cx('flex flex-col gap-2 rounded-xl border border-[var(--line)] p-3', f.status === 'archived' && 'opacity-60')}>
              <div className="flex items-start gap-2">
                <ClipboardList size={18} className="mt-0.5 shrink-0 text-[var(--ink-soft)]" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{f.title}</p>
                  <p className="flex flex-wrap gap-x-2 text-xs text-[var(--ink-soft)]">
                    <span>
                      {STATUS_TEXT[f.status]}
                      {f.activeVersion ? ` · Version ${f.activeVersion}` : ''}
                    </span>
                    <span>{f.submissionCount} Einsendung{f.submissionCount === 1 ? '' : 'en'}</span>
                    {f.repairIssues.length > 0 && <span className="text-rose-700 dark:text-rose-300">Reparatur nötig</span>}
                    {f.status === 'paused' && f.pauseReason === 'board_archived' && <span>Board war archiviert</span>}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {data?.canManage && (
                  <Button size="sm" onClick={() => navigate(`/b/${boardId}/formulare/${f.id}`)}>
                    <Pencil size={14} /> Bearbeiten
                  </Button>
                )}
                {f.status === 'active' && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => navigate(f.fillPath)}>
                      Ausfüllen
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        await navigator.clipboard?.writeText(`${location.origin}${f.fillPath}`).catch(() => undefined);
                        toast.info('Link kopiert. Er funktioniert für Personen mit Zugang zum Formular.');
                      }}
                    >
                      <Copy size={14} /> Link
                    </Button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
      <NewFormDialog open={creating} onClose={() => setCreating(false)} boardId={boardId} columns={columns} />
    </Sheet>
  );
}

function NewFormDialog({ open, onClose, boardId, columns }: { open: boolean; onClose: () => void; boardId: string; columns: { id: string; title: string }[] }) {
  const { data: templates } = useQuery({ queryKey: ['form-templates'], queryFn: () => api.get<Tpl[]>('/api/form-templates'), enabled: open });
  const [tpl, setTpl] = useState<string>('anfrage');
  const [title, setTitle] = useState('');
  const [columnId, setColumnId] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setTpl('anfrage');
    setTitle('');
    // Eine Spalte „Eingang“ oder „Neue Anfragen“ vorschlagen, sonst die erste
    setColumnId((columns.find((c) => /eingang|anfrage|neu/i.test(c.title)) ?? columns[0])?.id ?? '');
  }, [open, columns]);
  const chosen = templates?.find((t) => t.key === tpl);
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()} title="Neues Formular" side="center">
      <form
        className="flex flex-col gap-4 p-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          try {
            const f = await api.post<FormSummary>(`/api/boards/${boardId}/forms`, { title: title.trim() || chosen?.name || 'Formular', template: tpl === 'leer' ? undefined : tpl, columnId });
            onClose();
            navigate(`/b/${boardId}/formulare/${f.id}`);
          } catch (err) {
            toast.error((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-medium">Vorlage</legend>
          <div className="grid gap-2 sm:grid-cols-2">
          {[...(templates ?? []), { key: 'leer', name: 'Leeres Formular', summary: 'Eine Seite, eine Frage – alles Weitere selbst', needs: { labels: [], fields: [] }, questions: 1 }].map((t) => (
            <label key={t.key} className={cx('flex cursor-pointer gap-3 rounded-xl border p-3 text-sm', tpl === t.key ? 'border-accent bg-accent-soft/50 ring-1 ring-accent' : 'border-[var(--line)]')}>
              <input type="radio" name="tpl" className="mt-1 size-4 accent-[var(--color-accent)]" checked={tpl === t.key} onChange={() => setTpl(t.key)} />
              <span className="flex flex-col gap-0.5">
                <span className="font-medium">{t.name}</span>
                <span className="text-xs text-[var(--ink-soft)]">{t.summary}</span>
              </span>
            </label>
          ))}
          </div>
        </fieldset>
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Name des Formulars
          <input className="min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 text-base font-normal sm:text-sm" maxLength={200} placeholder={chosen?.name ?? 'Formular'} value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1.5 text-sm font-medium">
          Neue Einsendungen landen in
          <select className="min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 text-base font-normal sm:text-sm" value={columnId} onChange={(e) => setColumnId(e.target.value)} required>
            {columns.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
          <span className="text-xs font-normal text-[var(--ink-soft)]">Am besten eine eigene Spalte wie „Eingang“ – ohne WIP-Limit.</span>
        </label>
        {chosen && (chosen.needs.labels.length > 0 || chosen.needs.fields.length > 0) && (
          <p className="rounded-lg bg-[var(--column)] p-2.5 text-xs">
            Falls noch nicht vorhanden, legt die Vorlage im Board an:{' '}
            {[...chosen.needs.labels.map((l) => `Label „${l.name}“`), ...chosen.needs.fields.map((f) => `Feld „${f.name}“`)].join(', ')}.
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" variant="primary" disabled={busy || !columnId}>
            Anlegen und bearbeiten
          </Button>
        </div>
      </form>
    </Sheet>
  );
}
